import { unref } from 'vue'
import { i18n } from '@/i18n'

// Helper to get current locale safely
const getCurrentLocale = (): string => {
  // Use unref to handle both Ref and plain string types
  return unref(i18n.global.locale)
}

// Date formatting
export function formatDate(date: Date | string | undefined | null, format: 'short' | 'long' | 'datetime' = 'short'): string {
  if (!date) return '-'

  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return '-'

  const locale = getCurrentLocale() === 'es' ? 'es-ES' : 'en-US'

  switch (format) {
    case 'short':
      return d.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    case 'long':
      return d.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    case 'datetime':
      return d.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    default:
      return d.toLocaleDateString(locale)
  }
}

export function formatDateTime(date: Date | string | undefined | null): string {
  return formatDate(date, 'datetime')
}

export function formatTime(date: Date | string | undefined | null): string {
  if (!date) return '-'

  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return '-'

  const locale = getCurrentLocale() === 'es' ? 'es-ES' : 'en-US'
  return d.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatRelativeTime(date: Date | string | undefined | null): string {
  if (!date) return '-'

  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return '-'

  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  const isSpanish = getCurrentLocale() === 'es'

  if (diffSec < 60) {
    return isSpanish ? 'hace un momento' : 'just now'
  } else if (diffMin < 60) {
    return isSpanish
      ? `hace ${diffMin} ${diffMin === 1 ? 'minuto' : 'minutos'}`
      : `${diffMin} ${diffMin === 1 ? 'minute' : 'minutes'} ago`
  } else if (diffHour < 24) {
    return isSpanish
      ? `hace ${diffHour} ${diffHour === 1 ? 'hora' : 'horas'}`
      : `${diffHour} ${diffHour === 1 ? 'hour' : 'hours'} ago`
  } else if (diffDay < 7) {
    return isSpanish
      ? `hace ${diffDay} ${diffDay === 1 ? 'dia' : 'dias'}`
      : `${diffDay} ${diffDay === 1 ? 'day' : 'days'} ago`
  } else {
    return formatDate(d, 'short')
  }
}

// Currency formatting
export function formatCurrency(
  amount: number | undefined | null,
  currency = 'USD'
): string {
  if (amount === undefined || amount === null) return '-'

  const locale = getCurrentLocale() === 'es' ? 'es-ES' : 'en-US'
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

export function formatNumber(
  value: number | undefined | null,
  decimals = 0
): string {
  if (value === undefined || value === null) return '-'

  const locale = getCurrentLocale() === 'es' ? 'es-ES' : 'en-US'
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}

export function formatPercentage(value: number | undefined | null): string {
  if (value === undefined || value === null) return '-'

  const locale = getCurrentLocale() === 'es' ? 'es-ES' : 'en-US'
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value / 100)
}

// String formatting
export function truncate(text: string | undefined | null, maxLength = 50): string {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

export function capitalize(text: string | undefined | null): string {
  if (!text) return ''
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
}

export function toTitleCase(text: string | undefined | null): string {
  if (!text) return ''
  return text
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Phone formatting
export function formatPhone(phone: string | undefined | null): string {
  if (!phone) return '-'
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '')
  // Format as (XXX) XXX-XXXX or similar
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  return phone
}

// File size formatting
export function formatFileSize(bytes: number | undefined | null): string {
  if (bytes === undefined || bytes === null) return '-'

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let unitIndex = 0
  let size = bytes

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }

  return `${size.toFixed(1)} ${units[unitIndex]}`
}

// Duration formatting
export function formatDuration(minutes: number | undefined | null): string {
  if (minutes === undefined || minutes === null) return '-'

  const isSpanish = getCurrentLocale() === 'es'

  if (minutes < 60) {
    return isSpanish ? `${minutes} min` : `${minutes} min`
  }

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60

  if (remainingMinutes === 0) {
    return isSpanish
      ? `${hours} ${hours === 1 ? 'hora' : 'horas'}`
      : `${hours} ${hours === 1 ? 'hour' : 'hours'}`
  }

  return isSpanish
    ? `${hours}h ${remainingMinutes}min`
    : `${hours}h ${remainingMinutes}min`
}

// Initials
export function getInitials(name: string | undefined | null): string {
  if (!name) return '??'
  return name
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2)
}
